/* eslint-disable react-refresh/only-export-components -- intentional: step exports a helper alongside the component */
import { useEffect, useMemo } from 'react';

import { cn } from '@/lib/cn';
import {
  PROVIDERS,
  TIER_LABEL,
  clampToProvider,
  estimateClipCostUsd,
  getProviderMeta,
  mattingAddOnUsd,
  type Duration,
  type Resolution,
} from '@/lib/editorCapabilities';
import type { VideoClipTransparency } from '@/services/avatarApi';

import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  merge: (patch: Partial<WizardState>) => void;
}

const TRANSPARENCY_OPTIONS: ReadonlyArray<{ id: VideoClipTransparency; label: string; blurb: string }> = [
  { id: 'none',       label: 'Original',     blurb: 'Opaque MP4 with the engine\'s natural backdrop.' },
  { id: 'chroma-key', label: 'Transparent',  blurb: 'Free, instant. Removes a synthesized magenta backdrop on-device.' },
  { id: 'matting',    label: 'True alpha',   blurb: 'Best quality, +$0.11 per 6s. Real per-pixel alpha via post-processing.' },
];

/**
 * Capability-gated picker. The (duration, resolution) pair is auto-clamped
 * whenever the user switches provider. Veo flags chroma-key as "auto-upgrades
 * to matting" so the user knows the choice is honored differently there.
 */
export function Step5EngineQuality({ state, update, merge }: Props) {
  const provider = useMemo(() => getProviderMeta(state.videoProvider), [state.videoProvider]);

  // When provider changes, clamp duration/resolution into its supported set.
  useEffect(() => {
    const safe = clampToProvider(provider, state.durationSec, state.resolution);
    if (safe.durationSec !== state.durationSec || safe.resolution !== state.resolution) {
      merge({ durationSec: safe.durationSec, resolution: safe.resolution });
    }
  }, [provider.id]);

  const baseCostUsd = estimateClipCostUsd(provider, state.durationSec, state.resolution);
  const mattingUsd = mattingAddOnUsd(state.durationSec, state.videoTransparency);
  const totalUsd = (baseCostUsd + mattingUsd) * state.variants;

  const veoAutoUpgrade =
    provider.chromaKeyAutoUpgrade === true && state.videoTransparency === 'chroma-key';

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">Engine &amp; quality</h1>
        <p className="text-sm text-fg-muted">
          Pick the engine that fits your budget and quality target. The options below auto-adapt to what the engine actually supports.
        </p>
      </header>

      {/* Engine grid */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">Engine</label>
        <div className="grid grid-cols-2 gap-2">
          {PROVIDERS.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => update('videoProvider', p.id)}
              className={cn(
                'flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                state.videoProvider === p.id
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg',
              )}
            >
              <span className="flex w-full items-center justify-between">
                <span className="font-bold">{p.label}</span>
                <span className="rounded-full bg-bg px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fg-muted">
                  {TIER_LABEL[p.tier]}
                </span>
              </span>
              <span className="text-[10px] text-fg-muted">{p.blurb}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Duration — provider-gated */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">Duration</label>
        <div className="flex gap-2">
          {provider.durations.map(d => (
            <button
              key={d}
              type="button"
              onClick={() => update('durationSec', d as Duration)}
              className={cn(
                'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                state.durationSec === d
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              {d}s
            </button>
          ))}
        </div>
      </div>

      {/* Resolution — provider-gated, hidden if only 1 supported */}
      {provider.resolutions.length > 1 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase text-fg-muted">Resolution</label>
          <div className="flex gap-2">
            {provider.resolutions.map(r => (
              <button
                key={r}
                type="button"
                onClick={() => update('resolution', r as Resolution)}
                className={cn(
                  'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                  state.resolution === r
                    ? 'border-brand bg-brand/10 text-fg'
                    : 'border-border bg-bg-secondary text-fg-muted',
                )}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Variants */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">Variants</label>
        <div className="flex gap-2">
          {([1, 2, 3] as const).map(n => (
            <button
              key={n}
              type="button"
              onClick={() => update('variants', n)}
              className={cn(
                'flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
                state.variants === n
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              {n}× {n === 1 ? '' : 'clips'}
            </button>
          ))}
        </div>
      </div>

      {/* Seed — optional integer for reproducible runs. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ed-seed" className="text-xs font-semibold uppercase text-fg-muted">
          Seed <span className="font-normal normal-case text-fg-muted/60">(optional)</span>
        </label>
        <input
          id="ed-seed"
          type="number"
          inputMode="numeric"
          value={state.videoSeed}
          onChange={(e) => update('videoSeed', e.target.value)}
          placeholder="Random"
          className="rounded-xl bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
        />
        <p className="text-[11px] text-fg-muted">
          Forces the same generator pseudo-randomness across runs. Useful to A/B engines or re-roll deterministically.
        </p>
      </div>

      {/* Transparency */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">Background</label>
        <div className="flex flex-col gap-1.5">
          {TRANSPARENCY_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => update('videoTransparency', opt.id)}
              className={cn(
                'flex items-start gap-2 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                state.videoTransparency === opt.id
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              <span className="flex flex-col">
                <span className="font-semibold text-fg">{opt.label}</span>
                <span className="text-[11px] text-fg-muted">{opt.blurb}</span>
              </span>
            </button>
          ))}
          {veoAutoUpgrade && (
            <p className="text-[11px] text-amber-400">
              {provider.label} doesn't accept synthetic backdrops — chroma-key will auto-upgrade to true alpha (matting) at runtime.
            </p>
          )}
        </div>
      </div>

      {/* Live cost */}
      <div className="rounded-xl bg-bg-secondary p-3 text-xs text-fg-muted">
        <p>
          Estimated cost:{' '}
          <span className="font-semibold text-fg">${totalUsd.toFixed(2)}</span>
          {' '}
          ({provider.label} · {state.durationSec}s · {state.resolution}
          {state.variants > 1 && ` · ${state.variants}×`}
          {state.videoTransparency === 'matting' && ` + matting`})
        </p>
      </div>
    </section>
  );
}

export function step5Valid(state: WizardState): boolean {
  return !!state.videoProvider;
}
